import { IAddOn, IPrivateListing } from '../listings/mock-listings.data';
import { IBooking } from '@cnt-workspace/models';

/** Real bookings (from BookingService) whose listing belongs to the host.
 *  `myListings` comes from `HostListingsService.myListings$` (real backend
 *  ownership) — this stays param-driven like its siblings below. */
export function getHostBookings(myListings: IPrivateListing[], allBookings: IBooking[]): IBooking[] {
  const listingIds = new Set(myListings.map(l => l.id));
  return allBookings
    .filter(b => listingIds.has(b.listingId))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export interface IHostStats {
  earningsThisMonth: number;
  earningsYearToDate: number;
  upcomingNights: number;
  occupancyRate: number; // 0-100
  averageRating: number;
  totalReviews: number;
}

/** Derive host KPIs from real bookings + listings. No formula filler. */
export function getHostStats(listings: IPrivateListing[], hostBookings: IBooking[]): IHostStats {
  // Hosts only ever own private listings — boondocking sites aren't hostable, so the
  // caller's collection is already the right one; no narrowing needed.
  const privates = listings;
  const totalReviews = privates.reduce((s, l) => s + l.reviewCount, 0);
  const averageRating = privates.length === 0
    ? 0
    : +(privates.reduce((s, l) => s + l.rating, 0) / privates.length).toFixed(2);

  if (listings.length === 0) {
    return { earningsThisMonth: 0, earningsYearToDate: 0, upcomingNights: 0, occupancyRate: 0, averageRating, totalReviews };
  }

  const now = Date.now();
  const monthStart = new Date();
  monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
  const ninetyDaysAgo = now - 90 * 86_400_000;

  const counted = hostBookings.filter(b => b.status === 'confirmed' || b.status === 'approved');

  let earningsThisMonth = 0;
  let earningsYearToDate = 0;
  let upcomingNights = 0;
  let bookedNightsLast90 = 0;

  for (const b of counted) {
    const start = new Date(b.dates.start).getTime();
    const end = new Date(b.dates.end).getTime();
    if (start >= monthStart.getTime()) earningsThisMonth += b.total || 0;
    if (start >= yearStart) earningsYearToDate += b.total || 0;
    if (start >= now) upcomingNights += b.nights || 0;
    // Occupancy: overlap of [start, end] with [ninetyDaysAgo, now]
    const overlapStart = Math.max(start, ninetyDaysAgo);
    const overlapEnd = Math.min(end, now);
    if (overlapEnd > overlapStart) {
      bookedNightsLast90 += Math.ceil((overlapEnd - overlapStart) / 86_400_000);
    }
  }

  const availableNights = listings.length * 90;
  const occupancyRate = availableNights === 0
    ? 0
    : Math.min(100, Math.round((bookedNightsLast90 / availableNights) * 100));

  return {
    earningsThisMonth: Math.round(earningsThisMonth),
    earningsYearToDate: Math.round(earningsYearToDate),
    upcomingNights,
    occupancyRate,
    averageRating,
    totalReviews,
  };
}

// ─────────────────────── Per-add-on analytics ───────────────────────

/**
 * Per-add-on performance summary surfaced on the host dashboard. One row per
 * add-on the host currently offers across their listings (so unbooked rows
 * still appear with 0 metrics — hosts can spot the under-performers).
 */
export interface IAddOnPerformance {
  id: string;
  label: string;
  icon?: string;
  photo?: string;
  /** Number of confirmed/approved bookings that included this add-on. */
  bookingsCount: number;
  /** Total confirmed/approved bookings for the host — denominator for attachRate. */
  totalEligible: number;
  /** 0–100 (rounded). */
  attachRate: number;
  /** Sum of IBookingAddOn.amount across qualifying bookings. */
  totalRevenue: number;
  /** Sum of IBookingAddOn.quantity (useful for per-unit products). */
  totalUnits: number;
  /** ISO timestamp of the most recent qualifying booking; null when never booked. */
  lastBookedAt: string | null;
}

/**
 * Aggregate add-on performance across the host's listings + bookings.
 * Returns one entry per add-on the host *currently offers* (so the host can
 * see "Pet fee: 0%" and decide to drop or reprice). Sorted by revenue desc
 * with unbooked rows at the bottom.
 */
/**
 * How to get a listing's real add-ons (F12).
 *
 * These three call sites used to reach for `getListingDetail(l).addOns`, which
 * is the placeholder stub — it returns `[]` for anything but a listing published
 * on this very device, so the whole add-on performance widget was empty, the
 * add-on-to-property map was empty, and `offered` was always false. The caller
 * passes `HostListingsService.addOnsFor`, which is backed by the same entity
 * fetch the dashboard already makes.
 */
export type AddOnResolver = (listingId: number) => IAddOn[];

export function getAddOnPerformance(
  listings: IPrivateListing[],
  hostBookings: IBooking[],
  addOnsFor: AddOnResolver,
): IAddOnPerformance[] {
  // Build the catalog: every add-on the host currently offers, keyed by id.
  // Boondocking listings aren't hostable and can't carry user add-ons, so the
  // caller's collection (a host's own listings) is already the right one.
  const catalog = new Map<string, IAddOnPerformance>();
  for (const l of listings) {
    const addOns = addOnsFor(l.id);
    for (const a of addOns) {
      if (catalog.has(a.id)) continue; // first listing wins; ids are unique within a draft
      catalog.set(a.id, {
        id: a.id,
        label: a.label,
        icon: a.icon,
        photo: a.photo,
        bookingsCount: 0,
        totalEligible: 0,
        attachRate: 0,
        totalRevenue: 0,
        totalUnits: 0,
        lastBookedAt: null,
      });
    }
  }

  // Eligible bookings are the host's confirmed/approved set.
  const eligible = hostBookings.filter(b => b.status === 'confirmed' || b.status === 'approved');
  const totalEligible = eligible.length;

  for (const b of eligible) {
    if (!b.addOns?.length) continue;
    for (const a of b.addOns) {
      const row = catalog.get(a.id);
      // If a host removed an add-on after it was booked, we drop it from
      // the dashboard — the editor catalog wins. Matches "what's currently
      // offered" framing on the dashboard.
      if (!row) continue;
      row.bookingsCount += 1;
      row.totalRevenue += a.amount || 0;
      row.totalUnits += a.quantity || 1;
      if (!row.lastBookedAt || b.createdAt > row.lastBookedAt) {
        row.lastBookedAt = b.createdAt;
      }
    }
  }

  // Finalize attach rates and sort.
  const rows = [...catalog.values()];
  for (const r of rows) {
    r.totalEligible = totalEligible;
    r.attachRate = totalEligible === 0 ? 0 : Math.round((100 * r.bookingsCount) / totalEligible);
  }
  rows.sort((a, b) => {
    // Booked rows above unbooked; within each group sort by revenue desc.
    if ((a.bookingsCount > 0) !== (b.bookingsCount > 0)) return a.bookingsCount > 0 ? -1 : 1;
    return b.totalRevenue - a.totalRevenue;
  });
  return rows;
}

/** Per-listing breakdown of one add-on's performance. Drives the
 *  P3.2 / B drill-in on the host dashboard so the host can answer
 *  "which sites is this add-on actually selling on?" */
export interface IAddOnPerListingRow {
  listingId: number;
  title: string;
  /** True when the listing currently offers this add-on (the editor catalog wins). */
  offered: boolean;
  /** Bookings on this listing that included this add-on. */
  bookingsCount: number;
  /** Per-listing eligible bookings — denominator for attachRate. */
  eligibleCount: number;
  attachRate: number;             // 0–100, rounded
  totalUnits: number;
  totalRevenue: number;
}

/** Invert getAddOnPerformance for one add-on: rows are listings, not
 *  add-ons. Returns one row per listing the host owns — listings that
 *  don't offer the add-on appear with offered=false so the host can
 *  spot uneven coverage. */
export function getAddOnPerListingBreakdown(
  addOnId: string,
  listings: IPrivateListing[],
  hostBookings: IBooking[],
  addOnsFor: AddOnResolver,
): IAddOnPerListingRow[] {
  const rows = new Map<number, IAddOnPerListingRow>();
  for (const l of listings) {
    const offered = addOnsFor(l.id).some(a => a.id === addOnId);
    rows.set(l.id, {
      listingId: l.id,
      title: l.title,
      offered,
      bookingsCount: 0,
      eligibleCount: 0,
      attachRate: 0,
      totalUnits: 0,
      totalRevenue: 0,
    });
  }

  // Walk eligible bookings per listing, tallying both the eligible
  // denominator + the per-add-on numerator.
  const eligible = hostBookings.filter(b => b.status === 'confirmed' || b.status === 'approved');
  for (const b of eligible) {
    const row = rows.get(b.listingId);
    if (!row) continue;
    row.eligibleCount += 1;
    if (!b.addOns?.length) continue;
    for (const a of b.addOns) {
      if (a.id !== addOnId) continue;
      row.bookingsCount += 1;
      row.totalRevenue += a.amount || 0;
      row.totalUnits += a.quantity || 1;
    }
  }

  const out = [...rows.values()];
  for (const r of out) {
    r.attachRate = r.eligibleCount === 0 ? 0 : Math.round((100 * r.bookingsCount) / r.eligibleCount);
  }
  // Listings that don't offer the add-on sink to the bottom; within each
  // group, sort by revenue then attach rate.
  out.sort((a, b) => {
    if (a.offered !== b.offered) return a.offered ? -1 : 1;
    if (a.totalRevenue !== b.totalRevenue) return b.totalRevenue - a.totalRevenue;
    return b.attachRate - a.attachRate;
  });
  return out;
}
